/**
 * Admin Console HTTP client (gods only).
 *
 * Promise wrappers that resolve `{ ok, data | error, status }` so the
 * components can `await` without try/catch.  Backend routes:
 * `backend/app/routes/platform_route.py` (`GET /platform/workspaces`),
 * `backend/app/routes/platform_admin_route.py` (workspace detail, feature
 * flags, coupons) and `backend/app/routes/workspaces_route.py`
 * (`POST /workspaces`).  Every route answers 403 to non-gods; the client
 * only forwards the backend's `detail` text.
 */
import { sendJson } from '@/features/workspaces/api/workspacesApi';

const get = (suffix) => sendJson('GET', suffix);
const post = (suffix, body) => sendJson('POST', suffix, body);
const put = (suffix, body) => sendJson('PUT', suffix, body);

const encode = (value) => encodeURIComponent(String(value));

/** Every workspace on the platform with `owner_email`, `member_count`, `members` (`[{email, name}]`), `status`, `features`. */
export const fetchAllWorkspaces = () => get('platform/workspaces');

/** One workspace with its members (`{ workspace, members }`). */
export const fetchWorkspaceDetail = (workspaceId) => get(`platform/workspaces/${encode(workspaceId)}`);

/** Replace the workspace's enabled feature flags (SaaS only). Resolves `{ workspace_id, features }`. */
export const setWorkspaceFeatures = (workspaceId, features) =>
    put(`platform/workspaces/${encode(workspaceId)}/features`, { features });

/** God-created workspace (the god becomes its owner). Resolves `{ workspace }`. */
export const createWorkspaceAsGod = (name) => post('workspaces', { name: String(name || '').trim() });

/** Every platform coupon, newest first, with its redemption history (`{ coupons }`). */
export const fetchCoupons = () => get('platform/coupons');

/**
 * Create a coupon.  `workspaceId` `null` = any workspace may redeem it once
 * (optionally capped by `maxRedemptions`); a workspace-specific coupon is
 * single-use.  `code` `null` = the backend generates one.  `expiresAt` is a
 * `Date` or `null`.  Resolves `{ coupon }`.
 */
export const createCoupon = ({ amountUsd, workspaceId = null, code = null, maxRedemptions = null, expiresAt = null, note = null }) =>
    post('platform/coupons', {
        amount_usd: amountUsd,
        workspace_id: workspaceId || null,
        code: code || null,
        max_redemptions: maxRedemptions || null,
        expires_at: expiresAt instanceof Date ? expiresAt.toISOString() : expiresAt || null,
        note: note || null,
    });

/** Revoke a coupon so it can no longer be redeemed; past redemptions stay credited. Resolves `{ coupon }`. */
export const revokeCoupon = (couponId) => post(`platform/coupons/${encode(couponId)}/revoke`);
